import { Button, Loader, Modal, Select, Stack } from "@mantine/core";
import { useMap } from "@mantine/hooks";
import { IconTrash } from "@tabler/icons-react";
import { Suspense } from "react";
import { StrictUndef } from "shared/api/httpClient";
import { handleRoleAssign, UserSchema } from "shared/components/organization/users/data";
import { GroupEditComponent } from "shared/components/organization/users/GroupRoleManage";
import { IpawsEditPanel } from "shared/components/organization/users/IpawsRoleManage";
import { TopicEditPanel } from "shared/components/organization/users/TopicRoleManage";
import { features } from "shared/graphql/features";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { logger } from "shared/utils/logger";
import { graphql } from "src/gql";
import { FullUserFragment, PermissionType } from "src/gql/graphql";
import { match } from "ts-pattern";

export const RoleEditModal = ({ user, role, onClose }: { user: UserSchema; role: UserSchema["roles"][0]["role"]; onClose: () => void }) => {
    const hasTopics = features.enabled("HAS_TOPICS");
    const hasIpaws = features.enabled("HAS_IPAWS");

    const initialList = match(role.type)
        .with(PermissionType.Customer, () => undefined)
        .with(PermissionType.Group, () => user.roles.filter((r) => r.role.id == role.id).map((r) => r.group!.id))
        .with(PermissionType.Topic, () => (hasTopics ? user.roles.filter((r) => r.role.id == role.id).map((r) => r.topic!.id) : []))
        .with(PermissionType.Ipaws, () => (hasIpaws ? user.roles.filter((r) => r.role.id == role.id).map((r) => r.ipawsCert!.id!) : []))
        .exhaustive();

    const groupDiff = useMap<string, "add" | "remove">();

    const unassignRole = useGraphqlMutation(
        graphql(`
            mutation RoleEditUnassignRole($userId: UUID!, $userRoleId: UUID!) {
                unassignRole(userId: $userId, userRoleId: $userRoleId) {
                    ...FullUser
                }
            }
        `),
    );

    function confirmClose() {
        if (groupDiff.size > 0) {
            if (!window.confirm("You have unsaved changes. Continue?")) return;
        }
        onClose();
    }

    return (
        <Modal opened onClose={confirmClose} title="Edit Role" size="lg" closeButtonProps={{ "aria-label": "Close edit role modal" }}>
            <Stack>
                <Select label="Role" required defaultValue={role.name} data={[role.name]} disabled />
                <Suspense fallback={<Loader />}>
                    {match(role.type)
                        .with(PermissionType.Customer, () => (
                            <Button
                                w="100%"
                                leftSection={<IconTrash size={16} />}
                                onClick={() => {
                                    const userRoleId = user.roles.find((it) => it.role.id == role.id)?.id;
                                    if (!userRoleId) return;

                                    handleRoleAssign([
                                        unassignRole
                                            .mutateAsync({ userId: user.id, userRoleId })
                                            .then((res) => res.unassignRole as StrictUndef<FullUserFragment>),
                                    ])
                                        .then(onClose)
                                        .catch(logger.error);
                                }}
                            >
                                Remove Role Assignment
                            </Button>
                        ))
                        .with(PermissionType.Group, () => (
                            <GroupEditComponent
                                user={user}
                                selectedRoleId={role.id}
                                initialGroupIds={initialList!}
                                onClose={onClose}
                                groupDiff={groupDiff}
                            />
                        ))
                        .with(PermissionType.Topic, () => (
                            <TopicEditPanel user={user} selectedRoleId={role.id} initialTopicIds={initialList!} />
                        ))
                        .with(PermissionType.Ipaws, () => (
                            <IpawsEditPanel user={user} selectedRoleId={role.id} initialCertIds={initialList!} />
                        ))
                        .exhaustive()}
                </Suspense>
            </Stack>
        </Modal>
    );
};
